import React from 'react';
import { Box, Container, Typography, Grid, Button } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  GitHub,
  LinkedIn,
  Email as EmailIcon,
  ArrowOutward,
} from '@mui/icons-material';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const Heading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2rem, 5vw, 3rem)',
  fontWeight: 800,
  letterSpacing: '-0.03em',
  color: theme.palette.text.primary,
  marginBottom: '20px',
  maxWidth: '720px',
  lineHeight: 1.1,
}));

const Lede = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: theme.palette.text.secondary,
  marginBottom: '48px',
  maxWidth: '600px',
  lineHeight: 1.7,
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
    marginBottom: '32px',
  },
}));

const PrimaryCTA = styled(Button)(({ theme }) => ({
  padding: '14px 26px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '10px',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.default,
  marginRight: '12px',
  marginBottom: '12px',
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    opacity: 0.9,
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginRight: 0,
  },
}));

const ChannelCard = styled(Box)(({ theme }) => ({
  padding: '24px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  background: theme.palette.background.paper,
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  textDecoration: 'none',
  color: 'inherit',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, transform 0.2s ease',
  animation: `${fadeIn} 0.4s ease-out`,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    '& .arrow': {
      transform: 'translate(4px, -4px)',
    },
  },
  '& .arrow': {
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s ease',
    marginLeft: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    padding: '18px',
  },
}));

const ChannelIcon = styled(Box)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.primary,
  flexShrink: 0,
}));

const ChannelLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: '4px',
}));

const ChannelValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  wordBreak: 'break-all',
}));

const Contact = () => {
  const channels = [
    {
      label: 'Email',
      value: 'david.owuor@ndsu.edu',
      href: 'mailto:david.owuor@ndsu.edu',
      icon: <EmailIcon fontSize="small" />,
    },
    {
      label: 'LinkedIn',
      value: 'in/david-owuor',
      href: 'https://www.linkedin.com/in/david-owuor-082750201/',
      icon: <LinkedIn fontSize="small" />,
    },
    {
      label: 'GitHub',
      value: 'DMasilva',
      href: 'https://github.com/DMasilva',
      icon: <GitHub fontSize="small" />,
    },
  ];

  return (
    <Section id="contact">
      <Container maxWidth="lg">
        <Eyebrow>Contact</Eyebrow>
        <Heading>Let's talk.</Heading>
        <Lede>
          I'm always up for a conversation about interesting engineering
          problems, software testing research, or roles that pair good product
          thinking with hard technical work. Email is the fastest way to reach
          me.
        </Lede>

        <Box sx={{ mb: 6 }}>
          <PrimaryCTA
            href="mailto:david.owuor@ndsu.edu"
            disableElevation
            endIcon={<ArrowOutward sx={{ fontSize: '1rem !important' }} />}
          >
            Send me an email
          </PrimaryCTA>
        </Box>

        <Grid container spacing={2}>
          {channels.map((c) => (
            <Grid item xs={12} md={4} key={c.label}>
              <ChannelCard
                component="a"
                href={c.href}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel={c.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                <ChannelIcon>{c.icon}</ChannelIcon>
                <Box sx={{ flex: 1 }}>
                  <ChannelLabel>{c.label}</ChannelLabel>
                  <ChannelValue>{c.value}</ChannelValue>
                </Box>
                <ArrowOutward className="arrow" sx={{ fontSize: '1.125rem' }} />
              </ChannelCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Contact;
